/**
 * TabPhone Component - React 19 Migration
 *
 * Displays a paginated table of phone call appointments with filtering and navigation.
 *
 * MIGRATION CHANGES FROM REACT 16 TO 19:
 * =====================================
 *
 * 1. MATERIAL-UI v4 → MUI v5 (@mui/material)
 *    - Updated imports from '@material-ui/core' to '@mui/material'
 *    - Replaced makeStyles/withStyles with styled-components for better performance
 *    - MUI v5 uses emotion/styled-components instead of JSS
 *
 * 2. STYLED COMPONENTS APPROACH
 *    - withStyles(theme => ({...}))(Component) → styled(Component)`...`
 *    - makeStyles hook → styled components or sx prop
 *    - Theme access via props instead of useTheme in styled components
 *
 * 3. COMPONENT STRUCTURE
 *    - Extracted TablePaginationActions as a separate component for clarity
 *    - Moved PropTypes validation (React 19 still supports PropTypes for validation)
 *    - Better type safety preparation (can be converted to TypeScript later)
 *
 * 4. PERFORMANCE OPTIMIZATIONS
 *    - Memoized callbacks with useCallback to prevent unnecessary re-renders
 *    - Styled components are created outside component to avoid recreation
 *
 * 5. ACCESSIBILITY
 *    - Maintained all aria-labels and semantic HTML
 *    - Proper table structure with scope attributes
 *
 * COMPONENT FEATURES:
 * ==================
 * - Paginated table with configurable rows per page (5, 10, 25, All)
 * - Status display with internationalization
 * - Date formatting based on locale
 * - Analytics tracking on detail view navigation
 * - Responsive design with Material-UI Paper and Table components
 * - Empty state handling when no appointments exist
 *
 * @param {Array} phoneList - Array of phone appointment objects
 * @param {Function} setValue - Sets the active tab index for navigation
 * @param {Function} setPhoneCallId - Sets the selected appointment ID
 * @param {Function} setTypeAppointment - Sets appointment type ('phone')
 * @param {Function} setUserType - Sets user type ('CL' for client, 'AB' for other)
 */

import React, { useState, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import moment from 'moment';
import { GeneralContext } from '../../../../../../shared/contexts/GeneralContext.context';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TableFooter,
  TablePagination,
  IconButton,
  useTheme,
} from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import * as translations from './intl';
import { useTranslation } from '../../../../../../shared/hooks';
import { trativeFormatDateWithIdiom } from '../../../../../../shared/helpers';
import {
  getLanguage,
  getPartnerId,
  getPartnerName,
  getCountryCode,
  getInstanceEnv,
} from '../../../../../../shared/constants/globalConstants';

// =============================================================================
// STYLED COMPONENTS (Replaces makeStyles and withStyles from Material-UI v4)
// =============================================================================

/**
 * Styled Table Cell for warning messages (no bottom border)
 * Replaces: withStyles(() => ({ root: { borderBottom: 'none' } }))(TableCell)
 */
const StyledTableCellWarning = styled(TableCell)({
  borderBottom: 'none',
});

/**
 * Styled Table Cell with custom header and body styles
 * Replaces: withStyles(theme => ({ head: {...}, body: {...} }))(TableCell)
 */
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  '&.MuiTableCell-head': {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.text.main,
    fontFamily: "'Source Sans Pro'",
    fontStyle: 'normal',
    fontWeight: 600,
    fontSize: '12px',
    lineHeight: '16px',
    letterSpacing: '1px',
    boxShadow: '0px 1px 0px #111b1d',
  },
  '&.MuiTableCell-body': {
    color: theme.palette.text.main,
    fontSize: '16px',
    fontFamily: "'Source Sans Pro'",
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '24px',
  },
}));

/**
 * Styled Table Row with alternating background colors
 * Replaces: withStyles(theme => ({ root: { '&:nth-of-type(odd)': {...} } }))(TableRow)
 */
const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  cursor: 'pointer',
  '&:hover': {
    opacity: 0.8,
  },
}));

/**
 * Styled Table Container with minimum width
 * Replaces: makeStyles(() => ({ table: { minWidth: 700 } }))
 */
const StyledTable = styled(Table)({
  minWidth: '700px',
});

/**
 * Pagination Actions Container
 * Replaces: makeStyles(theme => ({ root: { flexShrink: 0, marginLeft: theme.spacing(2.5) } }))
 */
const PaginationActionsContainer = styled('div')(({ theme }) => ({
  flexShrink: 0,
  marginLeft: theme.spacing(2.5),
}));

/**
 * Styled Table Pagination
 * Fixes p tag margin from global CSS and adds responsive spacing
 */
const StyledTablePagination = styled(TablePagination)(({ theme }) => ({
  '& .MuiTablePagination-displayedRows, & .MuiTablePagination-selectLabel': {
    margin: 0,
  },
  [theme.breakpoints.down('sm')]: {
    '.MuiTablePagination-spacer': {
      display: 'none',
    },
  },
}));

// =============================================================================
// PAGINATION ACTIONS COMPONENT
// =============================================================================

/**
 * Custom Pagination Actions Component
 * Provides first, previous, next, and last page navigation buttons
 *
 * Extracted as separate component for:
 * - Better code organization
 * - Reusability
 * - Easier testing
 * - Cleaner main component
 */
function TablePaginationActions({
  count,
  page,
  rowsPerPage,
  onPageChange,
  labels,
}) {
  const theme = useTheme();

  const handleFirstPageButtonClick = useCallback(
    (event) => {
      onPageChange(event, 0);
    },
    [onPageChange]
  );

  const handleBackButtonClick = useCallback(
    (event) => {
      onPageChange(event, page - 1);
    },
    [onPageChange, page]
  );

  const handleNextButtonClick = useCallback(
    (event) => {
      onPageChange(event, page + 1);
    },
    [onPageChange, page]
  );

  const handleLastPageButtonClick = useCallback(
    (event) => {
      onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
    },
    [onPageChange, count, rowsPerPage]
  );

  return (
    <PaginationActionsContainer>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label={labels.FIRST_PAGE_LABEL}
      >
        {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label={labels.PREVIOUS_PAGE_LABEL}
      >
        {theme.direction === 'rtl' ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label={labels.NEXT_PAGE_LABEL}
      >
        {theme.direction === 'rtl' ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label={labels.LAST_PAGE_LABEL}
      >
        {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </PaginationActionsContainer>
  );
}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  labels: PropTypes.object.isRequired,
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

/**
 * TabPhone - Phone Appointments Table
 *
 * React 19 optimizations applied:
 * - useCallback for event handlers to prevent re-renders
 * - Memoized styled components (created outside component)
 * - Better component composition
 * - Modern MUI v5 patterns
 */
function TabPhone({
  phoneList,
  setValue,
  setPhoneCallId,
  setTypeAppointment,
  setUserType,
}) {
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [page, setPage] = useState(0);

  const { amosClient } = useContext(GeneralContext);
  const { locale: intl, idiom } = useTranslation({
    translationsFile: translations,
  });

  const {
    NAME,
    DATE,
    STATUS,
    TITLE_APPOINTMENT_PENDING,
    TITLE_APPOINTMENT_IN_PROGRESS,
    TITLE_APPOINTMENT_DONE,
    TITLE_APPOINTMENT_CANCELED,
    TITLE_APPOINTMENT_DETAILS,
    FIRST_PAGE_LABEL,
    PREVIOUS_PAGE_LABEL,
    NEXT_PAGE_LABEL,
    LAST_PAGE_LABEL,
    COSTUMIZED_TABLE_LABEL,
    ROWS_PER_PAGE_LABEL,
    TEXT_NOT_APPOINTMENT,
  } = intl;

  // Calculate empty rows for consistent table height
  const emptyRows =
    rowsPerPage - Math.min(rowsPerPage, phoneList.length - page * rowsPerPage);

  /**
   * Maps appointment status codes to translated status text
   * @param {string} typeStatus - Status code (done, pending, canceled, in-progress)
   * @returns {string} Translated status text
   */
  const onMakeStatus = useCallback(
    (typeStatus) => {
      switch (typeStatus) {
        case 'done':
          return TITLE_APPOINTMENT_DONE;
        case 'pending':
          return TITLE_APPOINTMENT_PENDING;
        case 'canceled':
          return TITLE_APPOINTMENT_CANCELED;
        case 'in-progress':
          return TITLE_APPOINTMENT_IN_PROGRESS;
        default:
          return '';
      }
    },
    [
      TITLE_APPOINTMENT_DONE,
      TITLE_APPOINTMENT_PENDING,
      TITLE_APPOINTMENT_CANCELED,
      TITLE_APPOINTMENT_IN_PROGRESS,
    ]
  );

  /**
   * Handles page change in pagination
   * useCallback prevents function recreation on every render
   */
  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  /**
   * Handles rows per page change
   * Resets to first page when changing rows per page
   */
  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  /**
   * Handles navigation to appointment details
   * Includes analytics tracking and state updates
   *
   * @param {Object} row - Appointment data object
   * @param {Event} event - Click event (used to stop propagation)
   */
  const handleDetails = useCallback(
    (row, event) => {
      event.stopPropagation();

      // Update parent component state
      setValue(2); // Navigate to details tab
      setPhoneCallId(row.id);
      setTypeAppointment('phone');

      // Set user type based on client ID match
      if (row.clientId === amosClient) {
        setUserType('CL');
      } else {
        setUserType('AB');
      }

      // Analytics tracking (Google Tag Manager)
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        subsidiary_name: 'axa_partners',
        business_unit: 'customer_and_solutions',
        business_line: 'travel_solutions',
        digital_business_process: 'assistance_request',
        global_digital_asset: 'doctor_please',
        partner_id: getPartnerId(),
        partner_name: getPartnerName(),
        country: getCountryCode(),
        language: getLanguage(),
        instance_environment: getInstanceEnv(),
        user_logged: 'true',
        page_name: 'appointments details',
        appointment_type: 'phone call',
        appointment_status: row.status,
        funnel_step: '4',
        step_name: 'appointment details',
        event: 'pageview',
      });
    },
    [setValue, setPhoneCallId, setTypeAppointment, setUserType, amosClient]
  );

  // Get paginated rows
  const displayedRows =
    rowsPerPage > 0
      ? phoneList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : phoneList;

  return (
    <TableContainer component={Paper}>
      <StyledTable aria-label={COSTUMIZED_TABLE_LABEL}>
        {/* Table Header */}
        <TableHead>
          <TableRow>
            <StyledTableCell>{NAME}</StyledTableCell>
            <StyledTableCell align="left">{STATUS}</StyledTableCell>
            <StyledTableCell align="left">{DATE}</StyledTableCell>
            <StyledTableCell></StyledTableCell>
          </TableRow>
        </TableHead>

        {/* Table Body */}
        <TableBody>
          {displayedRows.map((row) => (
            <StyledTableRow
              key={row.id}
              onClick={(event) => handleDetails(row, event)}
            >
              <StyledTableCell component="th" scope="row">
                {`${row.patient.name} ${row.patient.surname2}`}
              </StyledTableCell>
              <StyledTableCell align="left">
                {onMakeStatus(String(row.status).toLowerCase())}
              </StyledTableCell>
              <StyledTableCell align="left">
                {moment(row.date).format(trativeFormatDateWithIdiom(idiom))}
              </StyledTableCell>
              <StyledTableCell>
                <Button
                  color="primary"
                  endIcon={<ArrowForwardRoundedIcon />}
                  onClick={(event) => handleDetails(row, event)}
                >
                  {TITLE_APPOINTMENT_DETAILS.toUpperCase()}
                </Button>
              </StyledTableCell>
            </StyledTableRow>
          ))}

          {/* Empty State - Shows when no appointments exist */}
          {emptyRows === 5 && (
            <TableRow style={{ height: 53 * emptyRows }}>
              <StyledTableCellWarning colSpan={5} className="d-flex">
                <div>{TEXT_NOT_APPOINTMENT}</div>
              </StyledTableCellWarning>
            </TableRow>
          )}
        </TableBody>

        {/* Table Footer with Pagination */}
        <TableFooter>
          <TableRow>
            <StyledTablePagination
              rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
              count={phoneList.length}
              rowsPerPage={rowsPerPage}
              page={page}
              labelRowsPerPage={ROWS_PER_PAGE_LABEL}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={(props) => (
                <TablePaginationActions
                  {...props}
                  labels={{
                    FIRST_PAGE_LABEL,
                    PREVIOUS_PAGE_LABEL,
                    NEXT_PAGE_LABEL,
                    LAST_PAGE_LABEL,
                  }}
                />
              )}
            />
          </TableRow>
        </TableFooter>
      </StyledTable>
    </TableContainer>
  );
}

// PropTypes for runtime validation (still useful in React 19)
TabPhone.propTypes = {
  phoneList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      patient: PropTypes.shape({
        name: PropTypes.string.isRequired,
        surname2: PropTypes.string.isRequired,
      }).isRequired,
      status: PropTypes.string.isRequired,
      date: PropTypes.string.isRequired,
      clientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ).isRequired,
  setValue: PropTypes.func.isRequired,
  setPhoneCallId: PropTypes.func.isRequired,
  setTypeAppointment: PropTypes.func.isRequired,
  setUserType: PropTypes.func.isRequired,
};

export default TabPhone;
